import {API} from 'common/api';
import type {InterviewExperienceSubmitData} from 'iq-interview_experiences/types';

// Take-home file validation lives in the iq-takehomes domain; re-exported here
// so the submission flow can validate take-home attachments.
export {validateTakehomeFiles} from 'iq-takehomes/api';
export type {TakehomeFileMeta, TakehomeValidationResult} from 'iq-takehomes/api';

type ValidationResult = {success: true} | {success: false; reason: string};
type StepValidationResult = {valid: boolean; reason: string};

/** Maps frontend submit data to the backend SubmitExperienceRequest shape. */
function toBackendPayload(data: InterviewExperienceSubmitData) {
  return {
    company: data.company,
    position: data.position,
    experience: data.experience,
    outcome: data.outcome,
    outcome_color: data.outcome_color,
    interview_date: data.date, // backend field name differs from frontend
    ...(data.questions ? {questions: data.questions} : {}),
    ...(data.location ? {location: data.location} : {}),
    ...(data.take_home_files.length ? {take_home_files: data.take_home_files} : {}),
    ...(data.submission_flow_id ? {submission_flow_id: data.submission_flow_id} : {}),
    ...(data.guide_slug ? {guide_slug: data.guide_slug} : {}),
    ...(data.source ? {source: data.source} : {}),
  };
}

const validateInterviewExperienceSubmitData = async (
  data: InterviewExperienceSubmitData,
): Promise<ValidationResult> => {
  const response = await API.post('/v1/interview-experiences/validate', toBackendPayload(data));
  if (response?.valid === true) {
    return {success: true};
  }
  return {
    success: false,
    reason: response?.feedback || response?.error || 'Unable to validate your submission. Please try again.',
  };
};

const validateInterviewExperienceStep = async (
  stepId: string,
  question: string,
  answer: string,
): Promise<StepValidationResult> => {
  const response = await API.post('/v1/interview-experiences/validate-step', {
    step_id: stepId,
    question,
    answer,
  });
  return {
    valid: response?.valid === true,
    reason: response?.reason ?? 'Unable to validate your answer. Please try again.',
  };
};

const submitInterviewExperience = async (data: InterviewExperienceSubmitData): Promise<void> => {
  await API.post('/v1/interview-experiences', toBackendPayload(data));
};

const getTakehomeUploadUrl = async (
  filename: string,
  contentType: string,
  submissionFlowId: string,
): Promise<{uploadUrl: string; s3Key: string; fields: Record<string, string>}> => {
  const response = await API.post('/v1/takehomes/upload-url', {
    filename,
    content_type: contentType,
    submission_flow_id: submissionFlowId,
  });
  return {
    uploadUrl: response.upload_url as string,
    s3Key: response.s3_key as string,
    fields: (response.fields as Record<string, string>) ?? {},
  };
};

export {submitInterviewExperience, validateInterviewExperienceSubmitData, validateInterviewExperienceStep, getTakehomeUploadUrl};
export type {ValidationResult, StepValidationResult};
